import { isAxiosError } from 'axios'
import { api } from '@/shared/api'
import { dropLocalGame, isLocalGame, localMove, localRoll, localView } from '@/shared/local/store'
import { forgetGame, rememberName, rememberSecret, secretOf } from './storage'
import type {
  ActiveGame,
  BotLevel,
  GameEntry,
  GameView,
  MoveStep,
  Variant,
  Visibility,
} from './types'

/**
 * Адрес партии, которая мешает начать новую.
 *
 * Сервер держит одну активную партию на вошедшего и отвечает 409 с адресом
 * прежней (GameController::oneAtATime). Ответ нужен не текстом, а адресом:
 * человеку предлагают либо вернуться туда, либо бросить её.
 */
export function busyWith(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null

  const gameId = (error.response.data as { gameId?: unknown }).gameId

  return typeof gameId === 'string' ? gameId : null
}

/** Партии по этому адресу больше нет — её убрал сервер или её никогда не было. */
export function isGone(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404
}

/**
 * Заголовок с секретом игрока — так сервер понимает, кто ходит.
 *
 * Наружу, потому что тем же секретом опознаётся говорящий в чате
 * (features/chat/api.ts): на сервере это одно и то же место
 * (ResolvesPlayer::viewer), и здесь оно тоже должно быть одно.
 */
export function playerAuth(gameId: string): Record<string, string> {
  const secret = secretOf(gameId)

  return secret === null ? {} : { 'X-Player-Secret': secret }
}

/**
 * Создать партию.
 *
 * Имя не обязательно: вошедшего в учётную запись сервер подписывает его
 * ником сам. Секрет всё равно сохраняем — он остаётся способом вернуться
 * в партию тем же игроком, а для гостя единственным.
 *
 * Открытый стол видно тем, кто ищет соперника; закрытый — только по ссылке.
 */
export async function createGame(
  name = '',
  visibility: Visibility = 'private',
  variant: Variant = 'classic',
): Promise<GameEntry> {
  const { data } = await api.post<GameEntry>('/games', {
    ...(name === '' ? {} : { name }),
    visibility,
    // Классика — не шлётся вовсе: сервер считает её умолчанием, и запрос
    // обычного стола остаётся тем же, что и до появления режимов.
    ...(variant === 'classic' ? {} : { variant }),
  })
  rememberSecret(data.id, data.secret, name)

  if (name !== '') rememberName(name)

  return data
}

/**
 * Быстрая игра: подсесть за открытый стол, а если такого нет — открыть свой.
 *
 * Одной ручкой, а не «найди, потом сядь»: между поиском и посадкой стол
 * успевает заполниться, и разбирать эту гонку пришлось бы здесь.
 */
export async function quickGame(name = ''): Promise<GameEntry> {
  const { data } = await api.post<GameEntry>('/games/quick', name === '' ? {} : { name })
  rememberSecret(data.id, data.secret, name)

  if (name !== '') rememberName(name)

  return data
}

export async function joinGame(gameId: string, name = ''): Promise<GameEntry> {
  const { data } = await api.post<GameEntry>(`/games/${gameId}/join`, name === '' ? {} : { name })
  rememberSecret(data.id, data.secret, name)

  if (name !== '') rememberName(name)

  return data
}

/**
 * Своя незаконченная партия — или null, если её нет.
 *
 * Сервер знает её только у вошедшего: гость опознаётся секретом, а секрет
 * у каждой партии свой, и «все партии этого человека» по нему не собрать
 * (App\Services\Ludo\GameService::activeFor). Партию гостя проверяет
 * useResumable — обычным запросом состояния.
 */
export async function fetchActiveGame(): Promise<ActiveGame | null> {
  const { data } = await api.get<{ game: ActiveGame | null }>('/games/active')

  return data.game
}

/**
 * Уйти из партии — из набора или из начатой.
 *
 * Партию, из которой ушёл последний живой игрок, сервер удаляет целиком, а
 * начатую — бросает. И там и там возвращаться уже некуда, поэтому запись
 * о партии из браузера убирается тем же движением.
 */
export async function leaveGame(gameId: string): Promise<void> {
  if (isLocalGame(gameId)) {
    dropLocalGame(gameId)
    forgetGame(gameId)

    return
  }

  await api.post(`/games/${gameId}/leave`, {}, { headers: playerAuth(gameId) })
  forgetGame(gameId)
}

/**
 * Страница незапущенного стола на месте — отметка присутствия в лобби.
 *
 * Кто давно не отмечался, сервер снимает с места (games:drop-absent): так
 * из-за стола уходит тот, кто закрыл вкладку или потерял связь.
 */
export async function markPresent(gameId: string): Promise<void> {
  await api.post(`/games/${gameId}/presence`, {}, { headers: playerAuth(gameId) })
}

/**
 * Страница стола закрывается — сигнал серверу, что игрок, скорее всего,
 * ушёл.
 *
 * Через sendBeacon: обычный запрос браузер обрывает вместе со страницей.
 * Beacon не умеет заголовков, поэтому секрет — в теле формой (форма, а не
 * JSON: JSON у beacon'а потребовал бы предварительного CORS-запроса, на
 * который у закрывающейся страницы нет времени). Сервер место сразу не
 * освобождает — ждёт несколько секунд: при обновлении страницы сигнал
 * приходит тоже, и вернувшаяся страница успевает отметиться.
 */
export function signalAway(gameId: string): void {
  const secret = secretOf(gameId)

  if (secret === null || typeof navigator.sendBeacon !== 'function') return

  navigator.sendBeacon(
    `${api.defaults.baseURL ?? ''}/games/${gameId}/away`,
    new URLSearchParams({ secret }),
  )
}

/**
 * Отметиться готовым — или передумать.
 *
 * Когда готовы все, кто сел, сервер начинает партию сам: ждать разрешения
 * от одного человека остальным не за что.
 */
export async function setReady(gameId: string, ready: boolean): Promise<GameView> {
  const { data } = await api.post<GameView>(
    `/games/${gameId}/ready`,
    { ready },
    { headers: playerAuth(gameId) },
  )

  return data
}

/**
 * Разбудить компьютер, если его ход завис.
 *
 * Ход бота держится на джобе, и она может пропасть: перезапустили воркера,
 * обновился код. Раз в минуту это чинит планировщик, но минута — вечность
 * для того, кто смотрит на доску.
 */
export async function nudgeGame(gameId: string): Promise<void> {
  await api.post(`/games/${gameId}/nudge`, {})
}

/** Посадить за стол компьютер. Право на это есть у хозяина стола. */
export async function addBot(gameId: string, level: BotLevel): Promise<GameView> {
  const { data } = await api.post<GameView>(
    `/games/${gameId}/bots`,
    { level },
    { headers: playerAuth(gameId) },
  )

  return data
}

/** Убрать компьютер. Место, а не идентификатор: их в ответе партии нет. */
export async function removeBot(gameId: string, seat: number): Promise<GameView> {
  const { data } = await api.delete<GameView>(`/games/${gameId}/bots/${seat}`, {
    headers: playerAuth(gameId),
  })

  return data
}

/**
 * Состояние партии.
 *
 * Партия за одним устройством считается здесь же: сервера у неё нет, а
 * экран партии не должен знать, кто посчитал ходы. Поэтому развилка одна и
 * стоит на самом краю — в ручках, а не в экране.
 */
export async function fetchGame(gameId: string): Promise<GameView> {
  if (isLocalGame(gameId)) return localView(gameId)

  const { data } = await api.get<GameView>(`/games/${gameId}`, { headers: playerAuth(gameId) })

  return data
}

export async function startGame(gameId: string): Promise<GameView> {
  const { data } = await api.post<GameView>(
    `/games/${gameId}/start`,
    {},
    { headers: playerAuth(gameId) },
  )

  return data
}

export async function rollDice(gameId: string): Promise<GameView> {
  if (isLocalGame(gameId)) return localRoll(gameId)

  const { data } = await api.post<GameView>(
    `/games/${gameId}/roll`,
    {},
    { headers: playerAuth(gameId) },
  )

  return data
}

/** Ход: один шаг или два — ход на сумму кубиков. */
export async function sendMove(gameId: string, steps: MoveStep[]): Promise<GameView> {
  if (isLocalGame(gameId)) return localMove(gameId, steps)

  const { data } = await api.post<GameView>(
    `/games/${gameId}/moves`,
    { steps },
    { headers: playerAuth(gameId) },
  )

  return data
}
